import { useEffect, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Press, cx } from '@dorixona/ui'
import { NOTICE_MS, useCart } from '../features/cart/CartProvider'

export interface QtyStepperProps {
  productId: number
  quantity: number
  /** Потолок «+»: `max_qty` товара или строки корзины — столько даёт одна аптека (R-26). */
  max: number
  onChange: (delta: number) => void
  className?: string
}

/**
 * «− N +» корзины — один на карточку, страницу товара и корзину, чтобы потолок
 * везде был одним и тем же. Упёрлись в остаток — «+» гаснет, но нажимается:
 * молчаливая кнопка выглядит сломанной, поэтому над счётчиком на пару секунд
 * всплывает «Доступно N шт.». То же — если сервер всё же отказал (остаток упал,
 * пока экран был открыт).
 *
 * Внутри плитки нажатие не открывает товар: касание до плитки не доходит.
 */
export function QtyStepper({ productId, quantity, max, onChange, className }: QtyStepperProps) {
  const { t } = useTranslation(['catalog', 'cart'])
  const cart = useCart()
  const limit = cart.maxOf(productId, max)
  const notice = cart.notice?.productId === productId ? cart.notice : null
  const full = quantity >= limit
  /** Нажали погасший «+» — своя подсказка, сервер тут ни при чём. */
  const [capped, setCapped] = useState(false)

  useEffect(() => {
    if (!capped) return
    const timer = setTimeout(() => {
      setCapped(false)
    }, NOTICE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [capped])

  const stop = (event: MouseEvent) => {
    event.stopPropagation()
  }

  return (
    <div className={cx('qty', className)} role="presentation" onClick={stop}>
      {notice ? (
        <span className="qty-note" role="status">
          {notice.available > 0
            ? t('cart:available', { count: notice.available })
            : t('catalog:outOfStock')}
        </span>
      ) : capped && full ? (
        <span className="qty-note" role="status">
          {t('cart:available', { count: limit })}
        </span>
      ) : null}
      <Press
        squish="key"
        haptic="key"
        aria-label={t('catalog:decrease')}
        onPress={() => {
          onChange(-1)
        }}
      >
        −
      </Press>
      <span className="num">{quantity}</span>
      <Press
        squish="key"
        aria-label={t('catalog:increase')}
        aria-disabled={full || undefined}
        haptic={full ? 'none' : 'key'}
        onPress={() => {
          if (full) setCapped(true)
          else onChange(1)
        }}
      >
        +
      </Press>
    </div>
  )
}
